import React, { useState } from "react"
import { format, fromUnixTime } from "date-fns"

import type { Token } from "@/domains/core"
import type { SortBy } from "@/domains/lock-page"
import type { LockDisplayData, LockTableRowData } from "@/domains/lock-page/components/card/0.story-mocks"
import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenFalse, ShowWhenTrue } from "@/components/conditionals"
import { Separator } from "@/components/separator"
import { MessageBoxIcon } from "@/domains/common/components/message-box-icon"
import { MultilineMessageBox } from "@/domains/common/components/multi-line-message-box"
import { BodyBase } from "@/domains/design/components/body-base"
import { BodySM } from "@/domains/design/components/body-sm"
import { cn } from "@/domains/library"
import { LockMergeVisualizer } from "@/domains/lock-page/components/card/merge-card/composables/lock-merge-visualizer/lock-merge-visualizer"
import { MergeExpiredPanel } from "@/domains/lock-page/components/card/merge-card/composables/merge-expired-panel/merge-expired-panel"
import { MergeNftSelector } from "@/domains/lock-page/components/card/merge-card/composables/merge-nft-selector/merge-nft-selector"

export interface MergeCardProps {
  nftId: number
  currentLock: LockDisplayData
  futureLock: LockDisplayData | null
  selectedNftId: number | null
  allOtherNFTs: Array<LockTableRowData>
  hasExpiryIncrease: boolean
  showClaimRewardsWarning: boolean
  hasExpired: boolean
  canMerge: boolean
  icon?: React.ReactElement
  governanceToken: Token
  vestedTokenSymbol: string
  onSelectNft: (nftId: number) => void
  onDeselectNft: () => void
  onMerge: () => void
  sortBy?: SortBy
  onSortChange?: (sortBy: SortBy) => void
}

export const MergeCard = ({
  nftId,
  currentLock,
  futureLock,
  selectedNftId,
  allOtherNFTs,
  hasExpiryIncrease,
  showClaimRewardsWarning,
  hasExpired,
  canMerge,
  icon,
  governanceToken,
  vestedTokenSymbol,
  onSelectNft,
  onDeselectNft,
  onMerge,
  sortBy,
  onSortChange,
}: MergeCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const selectedNft = allOtherNFTs.find((v) => v.id === selectedNftId)
  const hasViableSelection = !!selectedNft

  return (
    <Card.Container
      className={cn("flex w-full flex-col gap-6", "rounded-lg border border-p400-light bg-p400 p-4 lg:p-6")}
    >
      <Card.Header className="relative">
        {icon && (
          <Card.HeaderIconContainer>
            {React.cloneElement(icon, { className: "text-t200", size: 24 })}
          </Card.HeaderIconContainer>
        )}

        <div className="flex w-full flex-col">
          <Card.HeaderTitle>Merge</Card.HeaderTitle>
          <Card.HeaderDescription>Select to merge into #{nftId}</Card.HeaderDescription>
        </div>
      </Card.Header>
      <Separator
        className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
  after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
  after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
        decorative
      />

      <ShowWhenFalse when={hasExpired}>
        <div className={cn("flex items-center gap-3", hasViableSelection ? "justify-between" : "justify-start")}>
          <div className={hasViableSelection ? "order-1" : "order-2"}>
            <ShowWhenTrue when={hasViableSelection}>
              <BodyBase>
                Merging #{selectedNftId} into #{nftId}{" "}
                {hasExpiryIncrease && futureLock ? (
                  <span className="text-t100">
                    now expires on {format(fromUnixTime(futureLock.lockEnds), "MMM do, yyyy")}
                  </span>
                ) : (
                  <span className="text-t100">
                    {" "}
                    still expires on {format(fromUnixTime(currentLock.lockEnds), "MMM do, yyyy")}
                  </span>
                )}
              </BodyBase>
            </ShowWhenTrue>
            <ShowWhenTrue when={!hasViableSelection}>
              <BodyBase>to merge into #{nftId}</BodyBase>
            </ShowWhenTrue>
          </div>
          <div className={hasViableSelection ? "order-2" : "order-1"}>
            <MergeNftSelector
              nftId={nftId}
              selectedNftId={selectedNftId}
              allOtherNFTs={allOtherNFTs}
              isModalOpen={isModalOpen}
              governanceToken={governanceToken}
              vestedTokenSymbol={vestedTokenSymbol}
              sortBy={sortBy}
              onSelectNft={(id) => {
                onSelectNft(id)
                setIsModalOpen(false)
              }}
              onDeselectNft={onDeselectNft}
              onModalOpenChange={setIsModalOpen}
              onSortChange={onSortChange}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-1">
            {futureLock && (
              <LockMergeVisualizer
                currentLock={currentLock}
                futureLock={futureLock}
                vestedTokenSymbol={vestedTokenSymbol}
              />
            )}
          </div>
        </div>

        <ShowWhenTrue when={showClaimRewardsWarning}>
          <MultilineMessageBox icon={<MessageBoxIcon size={"md"} variant="error" />} variant="error">
            <BodySM className="text-error-text">
              Please claim your rewards before merging as veSCALE #{selectedNftId} will be burned and all of its
              unclaimed rewards will be stuck!
            </BodySM>
          </MultilineMessageBox>
        </ShowWhenTrue>
      </ShowWhenFalse>

      <ShowWhenTrue when={hasExpired}>
        <MergeExpiredPanel />
      </ShowWhenTrue>

      <Card.Buttons>
        <ShowWhenTrue when={!!selectedNft}>
          <Button variant={"primary"} onClick={onMerge} disabled={!canMerge}>
            Burn #{selectedNftId} and Merge into #{nftId}
          </Button>
        </ShowWhenTrue>

        <ShowWhenTrue when={!!!selectedNft}>
          <Button variant={"primary"} disabled={true}>
            Merge into #{nftId}
          </Button>
        </ShowWhenTrue>
      </Card.Buttons>
    </Card.Container>
  )
}
